import React, {
	useState,
	forwardRef,
	useImperativeHandle,
	useEffect,
} from 'react';
import classNames from 'classnames';
import { resetField, unMaskValue } from '../common-functions';
import { TextBoxInput } from '../input';
import { InputLabel } from '../common-components/input-label';
import { ErrorShower } from '../common-components/error-shower';
import Icon from '../../icon';
import NumberFormat from 'react-number-format';
import { sendGaOnlineErrorEvent } from '../../../../domains/forms/patient/enrollment-ga-events';
import { FormState } from 'react-hook-form';
import { IFormInput } from '../../../../domains/forms/patient/enrollment.models';
import {
	EventAction,
	EventCategory,
	EventType,
} from '../../../../../services/ga-enums-interfaces';
import InputComponent from './input-component';
import { UserActionType } from '../../analytics';

const useGAError = (
	errors: { [name: string]: { message: string | undefined } },
	fieldname: string,
	formState: FormState<IFormInput>
) => {
	const touchedFields = formState?.touched;
	const errorRef = React.useRef(errors[fieldname]?.message);
	errorRef.current = errors[fieldname]?.message;
	useEffect(() => {
		if (!errorRef.current) {
			return;
		}
		if (touchedFields && touchedFields[fieldname as keyof IFormInput]) {
			sendGaOnlineErrorEvent(errors, fieldname);
		}
	}, [errorRef.current]);

	const resetError = () => {
		errorRef.current = undefined;
	};

	return [resetError];
};

export const TextBox: React.FC<TextBoxInput> = forwardRef(
	(
		{
			label,
			name,
			type,
			required,
			max,
			maxLength,
			minLength,
			placeholder,
			help,
			ariaLabel,
			className,
			labelClassname,
			errors,
			register,
			setValue,
			value,
			mask,
			format,
			onChange,
			onFocus,
			onBlur,
			formState,
		},
		ref
	) => {
		const [showReset, setShowReset] = useState(false);
		useImperativeHandle(ref, () => ({
			updateShowReset: (inputValue: boolean) => {
				setShowReset(inputValue);
			},
		}));

		const [resetError] = useGAError(errors, name, formState);

		const onFocusHandler = (event: React.FocusEvent<HTMLInputElement>) => {
			resetError();
			if (onFocus) {
				onFocus(event);
			}
		};
		const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
			if (onBlur) {
				onBlur(event);
			}
		};

		return (
			<div className={className}>
				<InputLabel
					ariaLabel={ariaLabel}
					label={label}
					required={required}
					errors={errors}
					name={name}
					help={help}
				/>
				<div
					className={classNames('relative mt-2', {
						'border-2 border-error': errors[name],
					})}>
					{!format ? (
						<InputComponent
							errors={errors}
							type={type}
							className="appearance-none pl-7 pr-12 py-4 rounded-full w-full placeholder:text-font-400 text-font font-semibold outline-none shadow-leap focus:ring-2 focus:ring-brand"
							name={name}
							max={max}
							maxLength={maxLength}
							minLength={minLength}
							placeholder={placeholder}
							register={register}
							required={required}
							onChange={onChange}
							setShowReset={setShowReset}
							onFocusHandler={onFocusHandler}
							onBlurHandler={onBlurHandler}
							analytics={{
								userActionType: UserActionType.focus,
								eventType: EventType.SimpleEvent,
								eventCategory: EventCategory?.ProfileForm,
								eventAction: EventAction.FieldFocus,
								eventLabel: name,
								eventArgs: {},
							}}
						/>
					) : (
						<NumberFormat
							className="appearance-none px-7 py-4 rounded-full w-full placeholder:text-font-400 text-font font-semibold outline-none shadow-leap focus:ring-2 focus:ring-brand"
							id={`form-input-${name}`}
							aria-invalid={errors.name ? 'true' : 'false'}
							name={name}
							placeholder={placeholder}
							format={format}
							mask={mask}
							value={value}
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
								if (onChange) {
									onChange(event);
								}
								if (type === 'tel') {
									setShowReset(
										unMaskValue(event.target.value, '_', '(###) ###-####')
											.length > 0
									);
								} else {
									setShowReset(
										unMaskValue(
											event.target.value,
											['M', 'M', 'D', 'D', 'Y', 'Y', 'Y', 'Y'],
											'##/##/####'
										).length > 0
									);
								}
							}}
							onFocus={onFocusHandler}
							onBlur={onBlurHandler}
						/>
					)}
					<div className="absolute right-2 w-7 top-1/2 transform -translate-y-1/2 flex">
						{showReset ? (
							<button
								onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
									event.preventDefault();
									resetField(setValue!, name);
									setShowReset(false);
									return false;
								}}
								tabIndex={0}
								aria-label="clear field"
								className="cursor-pointer align-right">
								<Icon type="cross" className="w-4 text-font-300" />
							</button>
						) : (
							errors[name] && (
								<Icon
									aria-hidden={true}
									type="warning"
									viewBox="0 0 20 20"
									className={classNames('text-error w-5', {
										block: true,
									})}
								/>
							)
						)}
					</div>
				</div>
				{errors && errors[name] && (
					<div className="mt-2">
						<ErrorShower errors={errors} name={name} />
					</div>
				)}
			</div>
		);
	}
);
